import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Api as ApiIcon,
  Code as CodeIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectContext } from '../../contexts/ProjectContext';

interface AnalysisResult {
  script_id: string;
  name: string;
  download_url: string;
  collection_url: string;
  analysis_url: string;
}

const PostmanToJmxPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedProject } = useProjectContext();
  
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [scriptName, setScriptName] = useState('');
  const [scriptDescription, setScriptDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [jmxDownloadUrl, setJmxDownloadUrl] = useState<string | null>(null);
  const [collectionDownloadUrl, setCollectionDownloadUrl] = useState<string | null>(null);
  const [analysisDownloadUrl, setAnalysisDownloadUrl] = useState<string | null>(null);

  const steps = [
    'Upload Postman Collection',
    'Generate JMX Script',
    'Download Results'
  ];

  useEffect(() => {
    if (uploadedFile && !scriptName) {
      const fileName = uploadedFile.name.replace(/\.json$/i, '');
      setScriptName(fileName);
    }
  }, [uploadedFile, scriptName]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.json'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!validExtensions.includes(fileExtension)) {
        setUploadError('Please upload a valid Postman collection file (.json)');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File size must be less than 10MB');
        return;
      }
      
      setUploadedFile(file);
      setUploadError(null);
    }
  };

  const handlePostmanUpload = async () => {
    if (!uploadedFile) return;
    
    setUploading(true);
    setUploadError(null);
    
    try {
      const formData = new FormData();
      formData.append('collection', uploadedFile);
      formData.append('name', scriptName || uploadedFile.name.replace(/\.json$/i, ''));
      formData.append('description', scriptDescription);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/projects/${selectedProject!.id}/intelliscript/postman-to-jmx`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setActiveStep(2);
        setAnalysisResult(data.data);
        setJmxDownloadUrl(data.data.download_url);
        setCollectionDownloadUrl(data.data.collection_url);
        setAnalysisDownloadUrl(data.data.analysis_url);
      } else {
        setUploadError(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (url: string, filename: string) => {
      const token = localStorage.getItem('token');
      try {
      const response = await fetch(url, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
      } catch (error) {
      alert('Failed to download file');
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
  return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upload Postman Collection
        </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Upload your Postman collection file (.json) to generate a JMeter test script.
        </Typography>
              
              <Box sx={{ mb: 3 }}>
                <input
                  accept=".json"
                  style={{ display: 'none' }}
                  id="postman-file-upload"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="postman-file-upload">
          <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
            sx={{ mb: 2 }}
          >
                    Choose Postman Collection File
                  </Button>
                </label>
                
                {uploadedFile && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      icon={<ApiIcon />}
                      label={uploadedFile.name}
                      onDelete={() => setUploadedFile(null)}
                      color="primary"
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Size: {(uploadedFile.size / 1024).toFixed(2)} KB
            </Typography>
                  </Box>
          )}
              </Box>

          <TextField
            fullWidth
                label="Script Name"
            value={scriptName}
                onChange={(e) => setScriptName(e.target.value)}
            sx={{ mb: 2 }}
                placeholder="Enter a name for your JMX script"
          />
              
          <TextField
            fullWidth
                label="Description"
                value={scriptDescription}
                onChange={(e) => setScriptDescription(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 3 }}
                placeholder="Optional description for your script"
              />

              {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {uploadError}
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={handlePostmanUpload}
                disabled={!uploadedFile || uploading}
                startIcon={uploading ? <CircularProgress size={20} /> : <CodeIcon />}
                fullWidth
              >
                {uploading ? 'Processing...' : 'Generate JMX Script'}
              </Button>
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Processing Complete
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Your Postman collection has been successfully converted to a JMeter test script.
              </Typography>
              
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  JMX script generated successfully! The script includes:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Variable extraction from Postman environment and collection variables" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Request parameterization and correlation candidates" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Transaction controllers and proper JMX structure" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Extractors and assertions based on collection structure" />
                  </ListItem>
                </List>
              </Alert>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Download Results
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Download your generated JMX script and original collection file.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <CodeIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      JMX Script
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Generated JMeter test script with all requests and variables
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(jmxDownloadUrl!, `${scriptName}.jmx`)}
                      fullWidth
                    >
                      Download JMX
                    </Button>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <ApiIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      Original Collection
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Your uploaded Postman collection file
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(collectionDownloadUrl!, uploadedFile?.name || 'collection.json')}
                      fullWidth
                    >
                      Download Collection
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
                  onClick={() => {
                    setActiveStep(0);
                    setUploadedFile(null);
                    setScriptName('');
                    setScriptDescription('');
                    setAnalysisResult(null);
                    setJmxDownloadUrl(null);
                    setCollectionDownloadUrl(null);
                    setAnalysisDownloadUrl(null);
                  }}
            fullWidth
          >
                  Generate Another Script
          </Button>
        </Box>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  if (!selectedProject) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a project to continue.
            </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Postman to JMX
            </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Convert your Postman collection to a ready-to-use JMeter test script.
            </Typography>

      <Box sx={{ mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
          </Box>

      {renderStepContent(activeStep)}

      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            How it works
          </Typography>
          <Typography variant="body2" paragraph>
            The Postman to JMX converter analyzes your collection and automatically:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Extracts variables and parameters" 
                secondary="Collection variables, environment variables, and request parameters that should be parameterized"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Detects correlation candidates" 
                secondary="Authentication tokens, response values, and session data that need correlation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Generates proper JMX structure" 
                secondary="Transaction controllers, extractors, assertions, and user parameters"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Creates test scenarios" 
                secondary="Logical grouping of related requests based on folders and collections"
              />
            </ListItem>
          </List>
      </Paper>
      </Box>
    </Box>
  );
};

export default PostmanToJmxPage; 